import { MemoryInfo } from './types';

export interface MemoryEvent extends MemoryInfo {
    startTime: number;
}

interface MemoryMonitoringServiceOptions {
    context: Window;
    report: (event: MemoryEvent) => void;
    updateInterval?: number;
}

export interface MemoryMonitoringService {
    stop: () => void;
}

export const createMemoryMonitoringService = ({
    context,
    report,
    updateInterval = 1000,
}: MemoryMonitoringServiceOptions): MemoryMonitoringService | null => {
    let timer: number | null = null;

    if (!context.performance.memory) {
        return null;
    }

    timer = context.setInterval(() => {
        const event = createMemoryEvent(context);
        if (event) {
            report(event);
        }
    }, updateInterval);

    const stop = () => {
        if (timer) {
            context.clearInterval(timer);
        }
    };

    return {
        stop,
    };
};

export const createMemoryEvent = (context: Window): MemoryEvent | undefined => {
    if (!context.performance.memory) {
        return;
    }

    /*
     * Spreading performance.memory will return nothing:
     * const result = { ...window.performance.memory };
     * console.log(result); // {}
     * Destructuring individual properties solves the problem.
     * https://medium.com/@oryanmoshe/the-object-that-misbehaved-window-performance-memory-fe338736aed0
     */
    const { usedJSHeapSize, jsHeapSizeLimit, totalJSHeapSize } = context.performance.memory;
    return {
        usedJSHeapSize,
        jsHeapSizeLimit,
        totalJSHeapSize,
        startTime: context.performance.now(),
    };
};
